import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ShoppingCart, Search, Star, TrendingUp, Package, Download, Moon, Sun } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { useCart } from '../../context/CartContext';
import { useTheme } from '../../context/ThemeContext';
import Footer from '../../components/Footer';

const ShopHomePage = () => {
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [collections, setCollections] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [best, setBest] = useState([]);
  const [highlightedProductId, setHighlightedProductId] = useState(null);
  const { cart } = useCart();
  const { theme, toggleTheme } = useTheme();
  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  const navigate = useNavigate();
  
  // Product placeholder image
  const productPlaceholder = '/product-placeholder.jpg';

  useEffect(() => {
    const load = async () => {
      try {
        const [colsRes, featRes, bestRes] = await Promise.all([
          fetch(`${API_BASE}/api/shop/collections`),
          fetch(`${API_BASE}/api/shop/products?sort=newest&limit=8`),
          fetch(`${API_BASE}/api/shop/products?sort=best_selling&limit=8`),
        ]);
        const cols = (await colsRes.json()) || [];
        const featData = (await featRes.json())?.products || [];
        const bestsData = (await bestRes.json())?.products || [];
        
        // Normalize products - ensure id field exists
        const normalizeFeat = featData.map(p => ({ ...p, id: p.id || p._id }));
        const normalizeBest = bestsData.map(p => ({ ...p, id: p.id || p._id }));
        
        setCollections(cols);
        setFeatured(normalizeFeat);
        setBest(normalizeBest);
      } catch (e) {
        // ignore
      }
    };
    load();
    // Runs on mount and whenever the API base changes; `load` is defined inline.
  }, [API_BASE]);

  const onSearch = (e) => {
    e.preventDefault();
    if (!searchTerm.trim()) return;
    
    // Clear previous
    setHighlightedProductId(null);
    
    // Find ALL matching products (not just first one)
    const allProducts = [...featured, ...best];
    const matchingProducts = allProducts.filter(p => 
      p.title?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    if (matchingProducts.length === 0) {
      alert(`No product found matching "${searchTerm}"`);
      return;
    }
    
    // Highlight ALL matching products
    const matchingIds = matchingProducts.map(p => String(p.id));
    
    setTimeout(() => {
      setHighlightedProductId(matchingIds); // Set as array
      
      // Scroll to FIRST matching product
      const firstProductId = matchingIds[0];
      setTimeout(() => {
        const element = document.getElementById(`product-${firstProductId}`);
        if (element) {
          const rect = element.getBoundingClientRect();
          const scrollTop = window.pageYOffset + rect.top - (window.innerHeight / 2);
          window.scrollTo({ top: scrollTop, behavior: 'smooth' });
        }
      }, 300);
      
      // Remove highlight after 4 seconds
      setTimeout(() => {
        setHighlightedProductId(null);
      }, 4000);
    }, 100);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-red-50 to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800/80 dark:bg-gray-800/80 backdrop-blur-md shadow-lg sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center">
              <img 
                src={theme === 'dark' ? '/trainerlist-logo-dark.png' : '/trainerlist-logo.png'}
                alt="TrainerList" 
                className="h-10 sm:h-12 md:h-14 w-auto object-contain"
              />
            </Link>
            <nav className="flex items-center gap-6">
              <Link to="/" className="text-gray-700 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 font-semibold">Home</Link>
              <Link to="/trainers" className="text-gray-700 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 font-semibold">Trainers</Link>
              <Link to="/shop" className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-red-800 font-bold">Shop</Link>
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                aria-label="Toggle theme"
              >
                {theme === 'dark' ? (
                  <Sun className="w-5 h-5 text-yellow-500" />
                ) : (
                  <Moon className="w-5 h-5 text-gray-600" />
                )}
              </button>
              <Link to="/shop/cart" className="relative">
                <ShoppingCart className="w-6 h-6 text-red-700 dark:text-red-400" />
                <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {cart?.item_count || 0}
                </span>
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Banner */}
      <div className="bg-gradient-to-r from-red-600 via-red-800 to-red-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-5xl font-black mb-4">TrainerList Shop</h1>
          <p className="text-xl mb-8">Premium fitness products, workout plans, and nutrition guides from certified trainers</p>
          <div className="max-w-2xl mx-auto">
            <form className="flex gap-3" onSubmit={onSearch}>
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-200" />
                <Input
                  type="text"
                  placeholder="Search products, plans, or trainers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-12 pr-4 py-6 text-lg border-2 border-white/30 bg-white/10 text-white placeholder-gray-200 focus:border-white focus:bg-white/20 w-full"
                />
              </div>
              <button
                type="submit"
                onClick={onSearch}
                className="px-8 py-6 bg-white text-red-700 font-bold text-lg rounded-full hover:bg-red-50 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                Search
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Search Results Badge */}
        {searchParams.get('search') && (
          <div className="mb-6 text-center">
            <Badge className="bg-red-600 text-white text-lg px-6 py-2">
              Searching for: &ldquo;{searchParams.get('search')}&rdquo;
            </Badge>
          </div>
        )}
        
        {/* Featured Products */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold dark:text-gray-100">New Arrivals</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {featured.map((product) => {
              const productId = String(product.id);
              const isHighlighted = Array.isArray(highlightedProductId) 
                ? highlightedProductId.includes(productId)
                : highlightedProductId === productId;
              return (
              <Card 
                key={productId} 
                id={`product-${productId}`}
                className={`overflow-hidden hover:shadow-2xl transition-all transform hover:scale-105 border-2 dark:bg-gray-800 dark:border-gray-700 ${
                  isHighlighted
                    ? 'border-red-600 shadow-2xl ring-4 ring-red-300'
                    : 'border-transparent hover:border-red-300 dark:hover:border-red-600'
                }`}
              >
                <CardContent className="p-0">
                  <Link to={`/shop/products/${product.slug}`} className="block">
                    <div className="relative bg-white dark:bg-gray-700 overflow-hidden aspect-square cursor-pointer hover:opacity-90 transition-opacity">
                      <img 
                        src={(() => {
                          const mediaPath = (product.media && product.media[0]);
                          let imgSrc;
                          
                          if (mediaPath && !mediaPath.startsWith('http') && mediaPath !== '/product-placeholder.jpeg') {
                            // Handle different URL formats for Kubernetes routing
                            let cleanPath = mediaPath;
                            if (mediaPath.startsWith('/uploads/')) {
                              // Legacy format: /uploads/xxx.jpg -> /api/uploads/general/xxx.jpg
                              cleanPath = mediaPath.replace('/uploads/', '/api/uploads/general/');
                            } else if (!mediaPath.startsWith('/api/')) {
                              // Unknown format: prepend /api
                              cleanPath = `/api${mediaPath}`;
                            }
                            // If already starts with /api/, use as-is
                            imgSrc = `${process.env.REACT_APP_BACKEND_URL}${cleanPath}`;
                          } else {
                            imgSrc = mediaPath || productPlaceholder;
                          }
                          
                          if (product.title.includes("Premium")) {
                            console.log(`[SHOP] ${product.title} media:`, mediaPath);
                            console.log(`[SHOP] ${product.title} final URL:`, imgSrc);
                          }
                          return imgSrc;
                        })()}
                        alt={product.title} 
                        className={`w-full h-full ${
                          (!product.media || product.media.length === 0) 
                            ? 'object-contain p-4' 
                            : 'object-cover'
                        }`}
                        onLoad={(e) => {
                          if (product.title.includes("Premium")) {
                            console.log(`[SHOP] ✅ ${product.title} loaded:`, e.target.src);
                          }
                        }}
                        onError={(e) => {
                          if (product.title.includes("Premium")) {
                            console.error(`[SHOP] ❌ ${product.title} failed:`, e.target.src);
                          }
                          e.target.onerror = null;
                          e.target.src = productPlaceholder;
                          // Change to contain when placeholder is used
                          e.target.className = e.target.className.replace('object-cover', 'object-contain p-4');
                        }}
                      />
                    </div>
                  </Link>
                  <div className="p-4">
                    <Link to={`/shop/products/${product.slug}`} className="block hover:text-red-600 dark:hover:text-red-400 transition-colors">
                      <h3 className="font-bold text-lg mb-2 line-clamp-2 dark:text-gray-100">{product.title}</h3>
                    </Link>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="flex items-center">
                        <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                        <span className="ml-1 text-sm font-semibold dark:text-gray-200">{product.rating?.toFixed(1) || '0.0'}</span>
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">({product.review_count || 0})</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-black text-red-700 dark:text-red-400">${product.min_price?.toFixed(2) || '0.00'} <span className="text-sm font-normal text-gray-500 dark:text-gray-400">CAD</span></span>
                      <Link to={`/shop/products/${product.slug}`} className="text-red-700 dark:text-red-400 hover:text-black dark:hover:text-red-500 font-semibold">View →</Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
              );
            })}
          </div>
        </div>

        {/* Best Sellers */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold dark:text-gray-100">Best Sellers</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {best.map((product) => {
              const productId = String(product.id);
              const isHighlighted = Array.isArray(highlightedProductId) 
                ? highlightedProductId.includes(productId)
                : highlightedProductId === productId;
              return (
              <Card 
                key={productId}
                id={`product-${productId}`}
                className={`overflow-hidden hover:shadow-2xl transition-all transform hover:scale-105 border-2 dark:bg-gray-800 dark:border-gray-700 ${
                  isHighlighted
                    ? 'border-red-600 shadow-2xl ring-4 ring-red-300'
                    : 'border-transparent hover:border-red-300 dark:hover:border-red-600'
                }`}
              >
                <CardContent className="p-0">
                  <Link to={`/shop/products/${product.slug}`} className="block">
                    <div className="relative bg-white dark:bg-gray-700 overflow-hidden aspect-square cursor-pointer hover:opacity-90 transition-opacity">
                      <img 
                        src={(() => {
                          const mediaPath = (product.media && product.media[0]);
                          let imgSrc;
                          
                          if (mediaPath && !mediaPath.startsWith('http') && mediaPath !== '/product-placeholder.jpeg') {
                            // Handle different URL formats for Kubernetes routing
                            let cleanPath = mediaPath;
                            if (mediaPath.startsWith('/uploads/')) {
                              // Legacy format: /uploads/xxx.jpg -> /api/uploads/general/xxx.jpg
                              cleanPath = mediaPath.replace('/uploads/', '/api/uploads/general/');
                            } else if (!mediaPath.startsWith('/api/')) {
                              // Unknown format: prepend /api
                              cleanPath = `/api${mediaPath}`;
                            }
                            // If already starts with /api/, use as-is
                            imgSrc = `${process.env.REACT_APP_BACKEND_URL}${cleanPath}`;
                          } else {
                            imgSrc = mediaPath || productPlaceholder;
                          }
                          
                          if (product.title.includes("Premium")) {
                            console.log(`[SHOP] ${product.title} media:`, mediaPath);
                            console.log(`[SHOP] ${product.title} final URL:`, imgSrc);
                          }
                          return imgSrc;
                        })()}
                        alt={product.title} 
                        className={`w-full h-full ${
                          (!product.media || product.media.length === 0) 
                            ? 'object-contain p-4' 
                            : 'object-cover'
                        }`}
                        onLoad={(e) => {
                          if (product.title.includes("Premium")) {
                            console.log(`[SHOP] ✅ ${product.title} loaded:`, e.target.src);
                          }
                        }}
                        onError={(e) => {
                          if (product.title.includes("Premium")) {
                            console.error(`[SHOP] ❌ ${product.title} failed:`, e.target.src);
                          }
                          e.target.onerror = null;
                          e.target.src = productPlaceholder;
                          // Change to contain when placeholder is used
                          e.target.className = e.target.className.replace('object-cover', 'object-contain p-4');
                        }}
                      />
                    </div>
                  </Link>
                  <div className="p-4">
                    <Link to={`/shop/products/${product.slug}`} className="block hover:text-red-600 dark:hover:text-red-400 transition-colors">
                      <h3 className="font-bold text-lg mb-2 line-clamp-2 dark:text-gray-100">{product.title}</h3>
                    </Link>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="flex items-center">
                        <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                        <span className="ml-1 text-sm font-semibold dark:text-gray-200">{product.rating?.toFixed(1) || '0.0'}</span>
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">({product.review_count || 0})</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-black text-red-700 dark:text-red-400">${product.min_price?.toFixed(2) || '0.00'} <span className="text-sm font-normal text-gray-500 dark:text-gray-400">CAD</span></span>
                      <Link to={`/shop/products/${product.slug}`} className="text-red-700 dark:text-red-400 hover:text-black dark:hover:text-red-500 font-semibold">View →</Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
              );
            })}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ShopHomePage;
